import React from 'react';
import {Button} from 'semantic-ui-react';
import {get as _get} from 'lodash';

import {convertToInt} from '@utils/helpers';
import {getGeoRestrictionsForDeviceCategories} from '@utils/countryHelpers';
import {DEVICE_CATEGORY_DESKTOP, DEVICE_CATEGORY_MOBILE, DEVICE_CATEGORY_SMART_TV} from '@constants/variables';
import {getCountryTreeOptions, getSubdivisionTreeOptions} from '@modules/events/utils/eventGeoRestriction';

export const getEventBookingDataFromAllSteps = (stepsMethods, bookingId) => {
    const preparedData = {
        ...stepsMethods.getForms()['step1'].formValues,
        ...stepsMethods.getForms()['step2'].formValues,
        ...stepsMethods.getForms()['step3'].formValues,
    };

    return {
        id: convertToInt(bookingId),
        booking_type: convertToInt(preparedData.booking_type),
        client: convertToInt(preparedData.client),
        distribution: convertToInt(preparedData.distribution),
        client_package: preparedData.client_package ? convertToInt(preparedData.client_package) : null,
        is_hq: preparedData.is_hq || false,
        is_ll: preparedData.is_ll || false,
        price: null === preparedData.price ? null : parseFloat(preparedData.price),
        auto_update_price: null === preparedData.auto_update_price ? false : preparedData.auto_update_price,
        auto_update_geo_restrictions: preparedData.auto_update_geo_restrictions || false,
        invoice_status: !preparedData.invoice_status_toggle ? convertToInt(preparedData.invoice_status) : null,
        booking_geo_restrictions: getGeoRestrictionsForDeviceCategories(preparedData),
        auto_update_invoice_status: false,
    };
};

export const getAdditionalBackButton = (editForm, showPreviousStep) => {
    return !editForm && <Button
        color={'grey'}
        content={'Back'}
        icon={'angle double left'}
        labelPosition={'left'}
        onClick={showPreviousStep}
    />;
};

export const getDeviceNameById = (deviceId) => {
    switch (deviceId) {
        case DEVICE_CATEGORY_DESKTOP:
            return 'desktop';
        case DEVICE_CATEGORY_MOBILE:
            return 'mobile';
        case DEVICE_CATEGORY_SMART_TV:
            return 'smart_tv';
        default:
            return '';
    }
};

export const getFieldNamesByDeviceId = (deviceId) => {
    const deviceName = getDeviceNameById(deviceId);

    return {
        deviceName,
        switchFieldName: `${deviceName}_switch`,
        dropdownCountriesFieldName: `${deviceName}_countries`,
        dropdownSubdivisionsFieldName: `${deviceName}_subdivisions`,
    };
};

export const prepareForHandleSwitchChanged = ({
    props,
    setField,
    deviceId,
    value,
    initialCountryTreeOptions,
    initialSubdivisionTreeOptions,
}) => {
    const {
        GraphQLOptionsData: {
            continents,
            countries,
        },
    } = props;

    const {
        switchFieldName,
        dropdownCountriesFieldName,
        dropdownSubdivisionsFieldName,
    } = getFieldNamesByDeviceId(deviceId);
    let continentsList = [],
        countriesList = [],
        countryTreeOptions = initialCountryTreeOptions,
        subdivisionTreeOptions = initialSubdivisionTreeOptions;

    if (!value) {
        if (!countryTreeOptions) {
            countryTreeOptions = getCountryTreeOptions({
                continents: continents,
                selectedCountries: [],
            });
        }

        if (!subdivisionTreeOptions) {
            subdivisionTreeOptions = getSubdivisionTreeOptions({
                countries: countries,
                selectedCountries: [],
                selectedSubdivisions: [],
            });
        }

        continentsList = countryTreeOptions;
        countriesList = subdivisionTreeOptions;
    }

    setField(switchFieldName, {
        checked: !value,
        defaultValue: !value,
    });
    setField(dropdownCountriesFieldName, {
        hidden: value,
        options: continentsList,
    });
    setField(dropdownSubdivisionsFieldName, {
        hidden: true,
        options: countriesList,
    });

    return {
        countryTreeOptions,
        subdivisionTreeOptions,
    };
};

export const setCountryRestrictionsForDevices = ({devices, restrictions, continents, state, setState, setField}) => {
    devices.forEach(({deviceCategoryId, restrictionIndex, stateFlagName}) => {
        if (!state[stateFlagName]
            && continents
            && -1 < restrictionIndex
            && 0 < restrictions[restrictionIndex]?.country_iso_alpha2_codes?.length
        ) {
            setCountryEntityGeoRestrictions(deviceCategoryId, {
                continents,
                geoRestrictions: restrictions[restrictionIndex].country_iso_alpha2_codes,
            }, setField);

            setState(() => ({[stateFlagName]: true}));
        }
    });
};

export const setSubdivisionsRestrictionsForDevices = ({
    DEVICE_CATEGORIES,
    deviceByRestriction,
    restrictions,
    countries,
    state,
    setState,
    setField,
}) => {
    DEVICE_CATEGORIES.forEach((deviceCategoryId) => {
        const deviceName = DEVICE_CATEGORY_SMART_TV === deviceCategoryId
                ? 'smartTv'
                : getDeviceNameById(deviceCategoryId),
            deviceRestrictions = restrictions[deviceByRestriction[deviceCategoryId]],
            countryIsoCodes = _get(deviceRestrictions, 'country_iso_alpha2_codes', []),
            subdivisionIsoCodes = _get(deviceRestrictions, 'country_subdivision_iso_codes', []);

        if (!state[`${deviceName}SubdivisionsGenerated`]
            && countries
            && -1 < deviceByRestriction[deviceCategoryId]
            && checkSubdivisionsAnyCountry(countries, countryIsoCodes)
        ) {
            setSubdivisionsEntityGeoRestrictions(deviceCategoryId, {
                countries: countries,
                countriesGeoRestrictions: countryIsoCodes,
                subdivisionsGeoRestrictions: subdivisionIsoCodes,
            }, setField);

            setState(() => ({[`${deviceName}SubdivisionsGenerated`]: true}));
        }

        if (state[`${deviceName}SubdivisionsGenerated`]
            && !state[`${deviceName}SubdivisionsLoaded`]
            && 0 < subdivisionIsoCodes?.length
        ) {
            setSubdivisionsEntityGeoRestrictions(deviceCategoryId, {
                countries: countries,
                countriesGeoRestrictions: countryIsoCodes,
                subdivisionsGeoRestrictions: subdivisionIsoCodes,
            }, setField);

            setState(() => ({[`${deviceName}SubdivisionsLoaded`]: true}));
        }
    });
};

export const checkSubdivisionsAnyCountry = (countries = [], selectedCountries = []) => {
    for (const item of countries) {
        if (selectedCountries.includes(item.value)) {
            return true;
        }
    }

    return false;
};

export const setSubdivisionsEntityGeoRestrictions = (deviceId, data, setField) => {
    const deviceName = getDeviceNameById(deviceId);

    setField(`${deviceName}_subdivisions`, {
        hidden: false,
        options: getSubdivisionTreeOptions({
            countries: data.countries,
            selectedCountries: data.countriesGeoRestrictions || [],
            selectedSubdivisions: data.subdivisionsGeoRestrictions || [],
        }),
    });
};

export const setCountryEntityGeoRestrictions = (deviceId, data, setField) => {
    const deviceName = getDeviceNameById(deviceId);

    setField(`${deviceName}_switch`, {
        checked: true,
        defaultValue: true,
    });
    setField(`${deviceName}_countries`, {
        hidden: false,
        options: getCountryTreeOptions({
            continents: data.continents,
            selectedCountries: data.geoRestrictions,
        }),
    });
};
